import { LoadingSpinner } from "@cmp-src/loadings/LoadingSpinner";
import { cn } from "@gettactic/helpers";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import type { LucideIcon } from "lucide-react";
import * as React from "react";

const buttonVariants = cva(
    "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-brand font-inter text-button font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-80 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 cursor-pointer",
    {
        variants: {
            variant: {
                default:
                    "bg-primary text-primary-foreground hover:bg-primary-hover shadow-button disabled:bg-neutral-100 disabled:text-neutral-400 disabled:shadow-none",
                primary:
                    "bg-primary text-primary-foreground hover:bg-primary-hover shadow-button disabled:shadow-none",
                destructive:
                    "bg-destructive text-destructive-foreground hover:bg-destructive/90",
                destructiveOutline:
                    "bg-transparent border border-destructive text-destructive hover:bg-destructive/90 hover:text-destructive-foreground transition-colors",
                outline:
                    "border border-input bg-background hover:bg-accent hover:text-accent-foreground",
                trigger:
                    "flex border border-neutral-200 bg-background hover:bg-accent hover:text-accent-foreground max-w-full flex-row whitespace-nowrap rounded-lg",
                outlinePale:
                    "border-1 border-neutral-100 text-secondary-text bg-background hover:border-neutral-50 disabled:text-neutral-200",
                secondary: "bg-neutral-300 text-secondary-text hover:bg-neutral-400",
                outlineSecondary:
                    "bg-transparent border border-neutral-300 text-secondary-text hover:bg-neutral-100 transition-colors",
                secondaryDashboard:
                    "bg-neutral-100 text-secondary-text hover:bg-neutral-200",
                ghost: "hover:bg-accent hover:text-accent-foreground",
                link: "text-primary underline-offset-4 hover:underline",
                icon: "bg-transparent text-content hover:text-primary",
                circleIcon:
                    "bg-transparent text-content hover:text-primary h-9 w-9 rounded-full p-2 flex items-center justify-center transition-colors",
                info: "bg-blue-100 text-blue-800 hover:bg-blue-200 active:bg-blue-200",
                critical:
                    "bg-red-100 text-red-700 hover:bg-red-200 hover:text-red-900 active:bg-red-200 active:text-red-900",
                warning:
                    "bg-yellow-100 text-yellow-800 hover:bg-yellow-200 active:bg-yellow-200",
                success:
                    "bg-green-100 text-green-800 hover:bg-green-200 active:bg-green-200"
            },
            severity: {
                info: "bg-blue-100 text-blue-800 hover:bg-blue-200 focus:shadow-outline-blue",
                critical:
                    "bg-red-100 text-red-700 hover:bg-red-200 hover:text-red-900 focus:shadow-outline-red"
            },
            size: {
                default: "h-10 px-4 py-2",
                xs: "h-8 rounded-md px-2.5",
                sm: "h-9 rounded-md px-3",
                lg: "h-11 rounded-md px-8",
                xl: "h-12 px-10 py-3 rounded-lg",
                icon: "h-10 w-10",
                trigger: "max-h-[42px] h-[42px]"
            }
        },
        defaultVariants: {
            variant: "default",
            size: "default"
        }
    }
);

/** Icon can be a React node (JSX element) or a Lucide icon component */
type IconProp = React.ReactNode | LucideIcon;

type ButtonVariantProps = VariantProps<typeof buttonVariants>;

export interface ButtonProps
    extends React.ButtonHTMLAttributes<HTMLButtonElement>,
        ButtonVariantProps {
    /**
     * Visual intent of the button.
     *
     * - `default`: the primary action of a view, in the org brand colour. Turns grey when disabled.
     * - `primary`: same look as `default`, but keeps the brand colour (dimmed) when disabled.
     * - `outline`: secondary actions next to a primary one (Cancel, Back, Export). The most used variant.
     * - `ghost`: low-emphasis actions in toolbars, table rows and headers.
     * - `link`: an inline text action that reads like a link.
     * - `destructive` / `destructiveOutline`: irreversible actions (Delete, Remove, Revoke). Use the outline version when the destructive action is not the main one.
     * - `secondary`, `outlineSecondary`, `secondaryDashboard`, `outlinePale`: neutral surfaces for dense UIs and dashboard cards.
     * - `info`, `success`, `warning`, `critical`: tinted actions that live inside a status banner or alert of the same colour.
     * - `trigger`: internal to `FilterableSelect` and `CategorySelect`, which render their own trigger with it. Use those components instead.
     * - `icon`, `circleIcon`: bare icon buttons; pair them with `size="icon"` and an `aria-label`.
     *
     * @default "default"
     */
    variant?: ButtonVariantProps["variant"];
    /**
     * Height and padding.
     *
     * - `sm` (36px): the standard size inside cards, tables and toolbars. The most used size.
     * - `default` (40px): standalone actions and form submits.
     * - `lg` (44px) and `xl` (48px): hero or empty-state calls to action.
     * - `icon` (40×40): square, for icon-only buttons.
     * - `trigger` (42px, no padding): internal to `FilterableSelect` and `CategorySelect`, paired with `variant="trigger"`.
     * - `xs` (32px): compact actions inside dense rows and chips.
     *
     * @default "default"
     */
    size?: ButtonVariantProps["size"];
    /**
     * Legacy colour override that wins over `variant`.
     * Prefer `variant="info"` or `variant="critical"` in new code.
     * @deprecated Use `variant="info"` or `variant="critical"` instead.
     */
    severity?: ButtonVariantProps["severity"];
    /**
     * Meant to render the single child element instead of a `<button>` (Radix `Slot`).
     * Known issue: the label wrapper sits between the `Slot` and the child, so the button
     * styles are lost. Do not use it until that is fixed.
     * @default false
     */
    asChild?: boolean;
    /**
     * Shows a spinner in place of the label and disables the button.
     * The button keeps its width, so the layout does not jump while an action is pending.
     * The label stays in the accessibility tree (only visually hidden) and the button
     * gets `aria-busy`, so screen readers still announce what is loading.
     */
    isLoading?: boolean;
    /** Alias of `isLoading`, kept for existing call sites. Prefer `isLoading`. */
    loading?: boolean;
    /**
     * Icon rendered before the label. Pass a Lucide component (`iconLeft={Plus}`) to get
     * the default 16px sizing from `iconClassName`, or a JSX element for full control.
     */
    iconLeft?: IconProp;
    /** Icon rendered after the label. Accepts the same values as `iconLeft`. */
    iconRight?: IconProp;
    /** Extra classes for the `<span>` that wraps the label, e.g. to truncate long text. */
    labelClassName?: string;
    /**
     * Classes applied to icons passed as components to `iconLeft` / `iconRight`.
     * Ignored for icons passed as JSX elements.
     * @default "h-4 w-4"
     */
    iconClassName?: string;
}

/** Render an icon prop - handles both JSX elements and icon components */
const renderIcon = (
    icon: IconProp | undefined,
    defaultClassName: string
): React.ReactNode => {
    if (!icon) return null;

    // If it's already a valid React element (JSX), return as-is
    if (React.isValidElement(icon)) {
        return icon;
    }

    // If it's a component (function or forwardRef), render with default className
    if (
        typeof icon === "function" ||
        (typeof icon === "object" && icon !== null)
    ) {
        const IconComponent = icon as LucideIcon;
        return <IconComponent className={defaultClassName} />;
    }

    return icon;
};

/**
 * The standard action button of the Tactic web app, built on the design tokens so it
 * follows each organization's brand colour.
 *
 * - Renders `type="button"` unless told otherwise: pass `type="submit"` to submit a form.
 * - Icon-only buttons (`size="icon"`) need an `aria-label`.
 * - For icon-only table or row actions, prefer `IconButton`, which wraps this component.
 * - For confirmation dialogs, use `ConfirmModal` / `DeleteConfirmModal` instead of placing buttons by hand.
 */
const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
    (
        {
            className,
            variant,
            size,
            asChild = false,
            isLoading,
            loading,
            iconLeft,
            iconRight,
            children,
            severity,
            labelClassName,
            iconClassName = "h-4 w-4",
            ...props
        },
        ref
    ) => {
        const Comp = asChild ? Slot : "button";
        const isLoadingState = isLoading || loading;

        const renderedIconLeft = renderIcon(iconLeft, iconClassName);
        const renderedIconRight = renderIcon(iconRight, iconClassName);

        const content = (
            <>
                {renderedIconLeft && (
                    <span className="inline-flex">{renderedIconLeft}</span>
                )}
                {children && (
                    <span
                        className={cn(
                            "flex h-full w-full items-center justify-center gap-2",
                            labelClassName
                        )}
                    >
            {children}
          </span>
                )}
                {renderedIconRight && (
                    <span className="inline-flex">{renderedIconRight}</span>
                )}
            </>
        );

        return (
            <Comp
                className={cn(buttonVariants({ variant, size, severity, className }))}
                ref={ref}
                {...props}
                disabled={isLoadingState || props.disabled}
                aria-busy={isLoadingState || undefined}
                type={props.type || "button"}
            >
                {isLoadingState ? (
                    <span className="relative inline-flex items-center justify-center">
            <span className="opacity-0 transition-opacity duration-200">
              {content}
            </span>
            {/* The button keeps its label as its name; aria-busy says it is working */}
            <span
              aria-hidden
              className="absolute inset-0 flex items-center justify-center opacity-100 transition-opacity duration-200"
            >
              <LoadingSpinner variant="circular" />
            </span>
          </span>
                ) : (
                    content
                )}
            </Comp>
        );
    }
);
Button.displayName = "Button";

export { Button, buttonVariants };
